import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import classNames from 'classnames';
import fp from 'lodash/fp';
import ReactTable from 'react-table';
import ReactTableUtils from 'react-table/lib/utils';
import PropTypes from 'prop-types';
import { columnsProptype } from '../common/customPropTypes';

const TbodyComponent = ReactTableUtils.makeTemplateComponent('rt-tbody', 'Tbody');

const TbodyWithContextModal = ({ children, getContextRef, ...rest }) => (
    <TbodyComponent { ...rest }>
        <div ref={ getContextRef }>
            { children }
        </div>
    </TbodyComponent>
);

const px = (x) => `${x}px`;

const getColumnSpacing = (column, defaultColumnSpacing) => px(
    fp.isNumber(column.columnSpacing)
        ? column.columnSpacing
        : defaultColumnSpacing
);

const GridView = ({ styles, ...props }) => {
    const [redirectLink, setRedirectLink] = useState(null);
    const [clickCount, setClickCount] = useState(1);

    const wholeRowRedirect = (row, getRowLink) => (setRedirectLink(getRowLink(row)));

    const handleDoubleRowClick = (row, getRowLink) => {
        setClickCount(clickCount + 1);
        let singleClickTimer;
        if (clickCount === 1) {
            singleClickTimer = setTimeout(() => setClickCount(1), 300);
        }
        if (clickCount === 2) {
            setClickCount(0);
            clearTimeout(singleClickTimer);
            wholeRowRedirect(row, getRowLink);
        }
    };

    return (
        redirectLink
            ? (<Redirect to={ redirectLink } />)
            : (
                <>
                    { props.contextmodal }
                    <div className={ styles['react-table-container'] } ref={ props.contextRef }>
                        <div className={ classNames({
                            [styles['grid-loading-bar']]: true,
                            [styles.loading]: props.loading
                        }) }
                        />
                        <ReactTable
                            { ...props.paginationProps }
                            onSortedChange={ props.onSortedChange }
                            minRows={ props.minRows }
                            data={ props.data }
                            columns={ props.columns }
                            filterable={ props.filterable }
                            defaultFilterMethod={ props.defaultFilterMethod }
                            sortable={ false }
                            defaultSorted={ props.defaultSorted }
                            getTdProps={ (a, b, column) => ({
                                className: classNames({
                                    [styles.cell]: true,
                                    [styles.loading]: props.loading,
                                    [styles['column-spacing']]: true
                                }),
                                style: {
                                    overflow: column.overflow ? 'visible' : 'hidden',
                                    paddingRight: getColumnSpacing(column, props.defaultColumnSpacing)
                                }
                            }) }
                            getTrProps={ (state, rowInfo) => ({
                                className: classNames({
                                    [styles.row]: true,
                                    [styles.wholeRowLink]: props.singleClickRowLink,
                                    [rowInfo && props.getRowLevelClassName(rowInfo)]: true,
                                    [props.getRowClassName(rowInfo)]: true
                                }),
                                style: { minWidth: state.rowMinWidth, alignItems: 'center' },
                                onClick: () => {
                                    if (props.onRowClick && rowInfo) {
                                        props.onRowClick(rowInfo.original);
                                    }
                                    if (props.singleClickRowLink && rowInfo) {
                                        wholeRowRedirect(rowInfo, props.getRowLink);
                                    }
                                    if (props.doubleClickRowLink && rowInfo) {
                                        handleDoubleRowClick(rowInfo, props.getRowLink);
                                    }
                                }
                            }) }
                            getTbodyProps={ () => ({
                                className: classNames({ [styles.loading]: props.loading }),
                                getContextRef: props.getContextRef
                            }) }
                            getNoDataProps={ () => ({ className: classNames({ [styles.loading]: props.loading }) }) }
                            getTheadTrProps={ (state) => ({
                                className: styles['web-common-header-row'],
                                style: {
                                    minWidth: state.rowMinWidth,
                                    height: props.headerRowHeight
                                }
                            }) }
                            TbodyComponent={ TbodyWithContextModal }
                            getTheadThProps={ (a, b, column) => ({
                                className: classNames({
                                    [styles['web-common-header']]: true,
                                    [styles['wrap-header']]: fp.isBoolean(column.wrapHeader) || props.wrapHeaders,
                                    [styles.numberColumnHeader]: column.type === 'number',
                                    sortable: column.sortable
                                }),
                                style: {
                                    paddingRight: getColumnSpacing(column, props.defaultColumnSpacing)
                                }
                            }) }
                            getTheadGroupThProps={ (a, b, colGroup) => ({
                                style: {
                                    paddingRight: px(
                                        fp.isNumber(
                                            fp.last(colGroup.columns).columnSpacing
                                        ) ? fp.last(colGroup.columns).columnSpacing.columnSpacing : props.defaultColumnSpacing
                                    )
                                }
                            }) }
                            getTheadGroupProps={ () => ({
                                className: classNames({
                                    [styles['web-common-grid-thead']]: true,
                                    [styles['vertical-scrollbar-present']]: props.shouldShowScrollbar
                                })
                            }) }
                            ref={ props.getReactTableRef }
                            loadingText=""
                            className={ classNames({
                                '-striped': props.striped,
                                [styles['web-common-grid']]: true
                            }) }
                            getTheadProps={ () => ({
                                className: classNames({
                                    [styles['web-common-grid-thead']]: true,
                                    [styles['vertical-scrollbar-present']]: props.shouldShowScrollbar
                                })
                            }) }
                            getTableProps={ () => ({
                                className: classNames({
                                    [styles['web-common-grid-table']]: true,
                                    [styles.underlineLastRow]: props.underlineLastRow
                                })
                            }) }
                        />
                    </div>
                </>
            )
    );
};

GridView.propTypes = {
    columns: columnsProptype,
    defaultColumnSpacing: PropTypes.number,
    defaultSorted: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        desc: PropTypes.bool
    })),
    contextMenu: PropTypes.shape([]),
    getRowKey: PropTypes.func,
    defaultFilterMethod: PropTypes.func,
    doubleClickRowLink: PropTypes.bool,
    filterable: PropTypes.bool,
    getContextRef: PropTypes.func,
    getRowClassName: PropTypes.func,
    getRowLink: PropTypes.func,
    getRowLevelClassName: PropTypes.func,
    headerRowHeight: PropTypes.number,
    loading: PropTypes.bool,
    minRows: PropTypes.number,
    onRowClick: PropTypes.func,
    onSortedChange: PropTypes.func,
    singleClickRowLink: PropTypes.bool,
    striped: PropTypes.bool,
    styles: PropTypes.objectOf(PropTypes.string).isRequired,
    wrapHeaders: PropTypes.bool,
    underlineLastRow: PropTypes.bool,
    contextmodal: PropTypes.element,
    contextRef: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    shouldShowScrollbar: PropTypes.bool,
    getReactTableRef: PropTypes.func,
    paginationProps: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({})
    ])
};

GridView.defaultProps = {
    columns: null,
    defaultColumnSpacing: 0,
    defaultSorted: [],
    defaultFilterMethod: undefined,
    contextMenu: null,
    doubleClickRowLink: false,
    filterable: false,
    getContextRef: fp.noop(),
    getRowClassName: fp.constant(''),
    getRowKey: null,
    getRowLink: null,
    getRowLevelClassName: (rowInfo) => `level-${rowInfo.original.level}`,
    headerRowHeight: null,
    loading: false,
    minRows: undefined,
    onRowClick: null,
    onSortedChange: fp.noop,
    singleClickRowLink: false,
    striped: true,
    wrapHeaders: false,
    underlineLastRow: false,
    contextmodal: null,
    contextRef: null,
    data: [],
    shouldShowScrollbar: false,
    getReactTableRef: fp.noop,
    paginationProps: {}
};

TbodyWithContextModal.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node
    ]),
    getContextRef: PropTypes.func,
};

TbodyWithContextModal.defaultProps = {
    children: null,
    getContextRef: fp.noop
};

export default GridView;
